/**
 * Chunk-load recovery after the app has mounted.
 *
 * After a deploy, a screen loaded on demand (React.lazy / import()) can ask for
 * a JS/CSS file from the previous build that the server no longer has. The
 * server answers with index.html, the browser refuses it, and Vite dispatches
 * `vite:preloadError`. Left alone, the import rejects and the screen errors out.
 *
 * Here: when no bill is in progress, reload once so the till comes back on the
 * new build. When the cart has items (or the till is on a login/sync screen —
 * the same rule the service-worker update already follows), do nothing and let
 * the error surface exactly as it does today, so an in-progress bill is never
 * reloaded away. Shares its retry window with the pre-mount boot recovery in
 * index.html, so the two together can reload at most once per 2 minutes.
 */
import { hasActiveTransaction } from "../sw-register";
import { trace } from "./debugTrace";

export const BOOT_RECOVERY_KEY = "cw:bootRecoveryAt";
export const RECOVERY_WINDOW_MS = 120000;

export function shouldReloadForChunkError({ now, lastAttemptAt, activeTransaction }) {
  if (activeTransaction) return false;
  if (lastAttemptAt && now - lastAttemptAt < RECOVERY_WINDOW_MS) return false;
  return true;
}

export function installChunkLoadRecovery(win = window) {
  win.addEventListener("vite:preloadError", (event) => {
    let lastAttemptAt = 0;
    try {
      lastAttemptAt = Number(win.sessionStorage.getItem(BOOT_RECOVERY_KEY)) || 0;
    } catch {
      // storage unavailable — treat as no previous attempt
    }
    const activeTransaction = hasActiveTransaction();
    const now = Date.now();
    const reload = shouldReloadForChunkError({ now, lastAttemptAt, activeTransaction });
    trace("ChunkRecovery", "vite:preloadError", {
      message: event?.payload?.message,
      activeTransaction,
      lastAttemptAt,
      reload,
    });
    if (!reload) return;
    event.preventDefault();
    try {
      win.sessionStorage.setItem(BOOT_RECOVERY_KEY, String(now));
    } catch {
      // storage unavailable — still reload once
    }
    win.location.reload();
  });
}
